import { Left, Right } from 'purify-ts';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import { Refresh } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { assert, datetime, expr, sort, string } from '@codegrade/utils';
import client from '@/client';
import { Card } from '@/components/Card';
import CgError from '@/components/Error';
import { Icon } from '@/components/Icon';
import Loader from '@/components/Loader';
import { DescriptionPopover } from '@/components/Popover';
import RelativeTime from '@/components/RelativeTime';
// @ts-expect-error This component is not yet in TS
import SubmitButton from '@/components/SubmitButton';
import { AccessibleLogs } from '@/components/VisuallyHiddenLiveRegions';
import * as utils from '@/utils';
export default tsx.component({
    name: 'user-transactions',
    props: {
        user: p.ofType().required,
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            transactions: Maybe.empty(),
            usedCoupons: Maybe.empty(),
            tableId: `user-transaction-table-${utils.getUniqueId()}`,
            logs: [],
        };
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        userId() {
            return this.user.id;
        },
    },
    watch: {
        userId: {
            immediate: true,
            /** Wathcer to reload the data when the user id changes. */
            handler() {
                this.reloadPage();
            },
        },
        transactions: {
            immediate: true,
            /** Watcher on the transactions data property. */
            handler() {
                const msg = this.transactions.caseOf({
                    Nothing: () => 'Loading transactions...',
                    Just: either => either.caseOf({
                        Left: () => 'Encountered error while loading transactions. ', // Error message announced by CgError
                        Right: list => `${list.length} transactions loaded. `,
                    }),
                });
                this.logs = [...this.logs, msg];
            },
        },
        usedCoupons: {
            immediate: true,
            /** Watcher on the usedCoupons data property. */
            handler() {
                const msg = this.usedCoupons.caseOf({
                    Nothing: () => 'Loading used coupons...',
                    Just: either => either.caseOf({
                        Left: () => 'Encountered error while loading used coupons. ',
                        Right: list => `${list.length} coupons loaded. `,
                    }),
                });
                this.logs = [...this.logs, msg];
            },
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reloadPage() {
            Promise.all([
                this.reloadTransactions().catch(err => {
                    this.transactions = Maybe.of(Left(err));
                }),
                this.reloadUsedCoupons().catch(err => {
                    this.usedCoupons = Maybe.of(Left(err));
                }),
            ]);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async reloadTransactions() {
            const curUserId = this.userId;
            const data = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.user.getTransactions({
                userId: this.user.id,
                nextToken: nextToken.extract(),
            })));
            if (curUserId === this.userId) {
                this.transactions = Maybe.of(Right(data));
            }
        },
        /** @returns A list of coupons the user has utilized in the past. */
        async reloadUsedCoupons() {
            const curUserId = this.userId;
            const data = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.user.getCouponUsages({
                userId: this.user.id,
                nextToken: nextToken.extract(),
            })));
            if (curUserId === this.userId) {
                this.usedCoupons = Maybe.of(Right(data));
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async clickReloadBtn() {
            const btn = await this.$waitForRef('reload-btn');
            if (btn == null) {
                this.reloadPage();
            }
            else {
                btn.onClick();
            }
        },
        /**
         * Renders the refund button for a specific transaction if it is possible.
         *
         * @param transaction The transaction the refund button will refer to.
         * @returns The refund button if necessary.
         */
        maybeRenderRefundBtn(transaction) {
            if (transaction.state !== 'success' || transaction.successAt.isNothing()) {
                return Maybe.empty();
            }
            const popover = utils.transaction
                .canBeRefunded({
                ...transaction,
                successAt: transaction.successAt.value,
            }, this.$getRootInstance().$getNow)
                .caseOf({
                Just: Maybe.empty,
                Nothing: () => Maybe.of('This transaction can no longer be refunded'),
            });
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const askForRefund = () => client.transaction.refund({ transactionId: transaction.id }).map(response => response.addCallbacks({
                onAfterSuccess: () => this.clickReloadBtn(),
            }));
            return Maybe.of(<div v-b-popover_top_hover={popover.orDefault('')} class="my-n2">
                    <SubmitButton disabled={popover.isJust()} label="Request refund" confirm="Are you sure you want to refund this transaction? This means you can no longer access this course." submit={askForRefund}/>
                </div>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTransactionState(transaction) {
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const getPopover = () => {
                const state = transaction.state;
                switch (state) {
                    case 'pending':
                        return 'We are waiting for confirmation of the payment.';
                    case 'refunding':
                        return 'We are busy processing your refund.';
                    case 'refunded':
                        return 'We have refunded the transaction.';
                    case 'success':
                        return 'The payment was successful, and you can start with the course.';
                    case 'failed':
                        return 'The payment failed for some reason.';
                    default:
                        return assert.AssertionError.assertNever(state);
                }
            };
            return [
                string.capitalize(transaction.state),
                <DescriptionPopover ariaLabel="Show current transaction state">
                    {getPopover()}
                </DescriptionPopover>,
            ];
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTransaction(transaction) {
            return {
                key: `transaction-${transaction.shortId}`,
                type: <b-badge variant="primary">Transaction</b-badge>,
                ID: transaction.shortId,
                What: transaction instanceof api.CoursePurchase
                    ? this.renderCourseName(transaction.purchasedItem.course)
                    : transaction.successAt.caseOf({
                        Just: successAt => this.renderPlan(successAt, transaction.purchasedItem),
                        Nothing: () => <span>Access pass</span>,
                    }),
                'Updated at': (<RelativeTime date={transaction.successAt.orDefault(transaction.updatedAt)} tag="abbr"/>),
                State: this.renderTransactionState(transaction),
                Refund: this.maybeRenderRefundBtn(transaction),
            };
        },
        /**
         * Computes the RenderedSettlement.
         *
         * @param usedCoupon The used coupon to be rendered.
         * @returns The RenderedSettlement object with the content for a Tenant or Course Coupon.
         */
        renderUsedCoupon(usedCoupon) {
            const what = expr.match(usedCoupon, 'scope', {
                course: usage => this.renderCourseName(usage.coupon.coursePrice.course),
                tenant: usage => this.renderCourseName(usage.course),
                'access-plan': usage => this.renderPlan(usage.successAt, usage.coupon.plan),
            });
            return {
                key: `coupon-${usedCoupon.id}`,
                type: (<b-badge variant="primary">
                        {string.capitalize(usedCoupon.scope)} Coupon
                    </b-badge>),
                ID: usedCoupon.id,
                What: what,
                'Updated at': <RelativeTime date={usedCoupon.successAt} tag="abbr"/>,
                State: [
                    'Success',
                    <DescriptionPopover ariaLabel="View payment status">
                        The payment was successful, and you can start with the course.
                    </DescriptionPopover>,
                ],
                Refund: Maybe.empty(),
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderCourseName(courseInfo) {
            const route = {
                name: 'course_assignments',
                params: { courseId: `${courseInfo.id}` },
            };
            return (<router-link to={route} class="inline-link">
                    {courseInfo.name}
                </router-link>);
        },
        /**
         * Render a access plan.
         *
         * @param start The moment the plan was active.
         * @param plan The plan that was active.
         * @returns A node to display.
         */
        renderPlan(start, plan) {
            const end = start.clone().add(plan.duration);
            if (this.$getRootInstance().$getNow(end).isAfter(end)) {
                return <span>Access pass, expired at {datetime.readableFormatDate(end)}</span>;
            }
            return (<span>
                    Access pass, valid for <RelativeTime date={end} withoutSuffix/>
                </span>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderSettlementsTable(renderedSettlements) {
            const showRefund = renderedSettlements.some(x => x.Refund.isJust());
            return (<table id={this.tableId} class="table table-striped mb-0 min-w-120">
                    <thead>
                        <th class="shrink">Type</th>
                        <th>ID</th>
                        <th>What</th>
                        <th>Updated at</th>
                        <th class="shrink">State</th>
                        {showRefund && <th class="shrink">Refund</th>}
                    </thead>
                    <tbody>
                        {NonEmptyList.fromArray(renderedSettlements)
                    .map(ss => ss.map(s => (<tr key={s.key}>
                                        <td>{s.type}</td>
                                        <td>{s.ID}</td>
                                        <td>{s.What}</td>
                                        <td>{s['Updated at']}</td>
                                        <td class="shrink">{s.State}</td>
                                        {showRefund && (<td class="shrink">
                                                {s.Refund.orDefaultLazy(utils.emptyVNode)}
                                            </td>)}
                                    </tr>)))
                    .orDefaultLazy(() => (<tr>
                                    <td colspan={showRefund ? 6 : 5} class="text-muted text-center">
                                        No transactions yet
                                    </td>
                                </tr>))}
                    </tbody>
                </table>);
        },
        /**
         * Render the list of settlements the user was a part of.
         *
         * @param transactions List of transaction made by the user.
         * @param usedCoupons List of coupon used by the user.
         * @returns The rendered list of settlement the user was a part of.
         */
        renderSettlements(transactions, usedCoupons) {
            const settlements = sort.sortBy(transactions.concat(usedCoupons), (s) => {
                if (s instanceof api.CoursePurchase || s instanceof api.UserAccessPass) {
                    return [s.successAt, s.updatedAt];
                }
                else {
                    return [Maybe.of(s.successAt), s.successAt];
                }
            }, { reverse: false });
            const renderedSettlements = settlements.map(s => {
                if (s instanceof api.CoursePurchase || s instanceof api.UserAccessPass) {
                    return this.renderTransaction(s);
                }
                else if (s instanceof api.CourseCouponUsage ||
                    s instanceof api.AccessPassCouponUsage ||
                    s instanceof api.TenantCouponUsage) {
                    return this.renderUsedCoupon(s);
                }
                else {
                    return assert.AssertionError.assertNever(s);
                }
            });
            return (<div class="overflow-x-auto">
                    {this.renderSettlementsTable(renderedSettlements)}
                </div>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeRenderUsedCoupons(transactions, data) {
            return data.caseOf({
                Left: err => <CgError error={err} class="m-3"/>,
                Right: usedCoupons => this.renderSettlements(transactions, usedCoupons),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTransactions(transactions) {
            return this.usedCoupons
                .map(data => this.maybeRenderUsedCoupons(transactions, data))
                .orDefaultLazy(() => <Loader scale={1} class="m-3"/>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeRenderTransactions(data) {
            return data.caseOf({
                Left: err => <CgError error={err} class="m-3"/>,
                Right: transactions => this.renderTransactions(transactions),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeRenderReloadBtn() {
            return utils.ifJustOrEmpty(this.transactions, () => (<SubmitButton slot="header" submit={async () => {
                    this.logs.push('Reloading transactions...');
                    return await this.reloadTransactions();
                }} ref="reload-btn" class="min-h-8 min-w-8" aria-label="Refresh transactions">
                    <Icon icon={Refresh}/>
                    <Icon icon={Refresh} spin slot="pending-label"/>
                </SubmitButton>));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<Card class="user-transactions" header="Transactions">
                {this.maybeRenderReloadBtn()}
                <AccessibleLogs title="Logs for loading transaction data" logs={this.logs} for={this.tableId}/>
                {this.transactions
                .map(data => this.maybeRenderTransactions(data))
                .orDefaultLazy(() => (<Loader scale={1} class="m-3"/>))}
            </Card>);
    },
});
